import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import templateCategoryService from '../../../services/templateCategoryService';
import workflowTemplateService from '../../../services/workflowTemplateService';

const WorkflowTemplates = ({ isOpen, onClose, onTemplateSelect, onTemplateEdit }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categoryError, setCategoryError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [templateError, setTemplateError] = useState(null);

  useEffect(() => {
    loadCategories();
    loadTemplates();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      setCategoryError(null);
      const categoriesData = await templateCategoryService?.getAllCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading categories:', err);
      
      // Check if it's a table not found error
      if (err?.code === 'PGRST205' || err?.message?.includes('Could not find the table')) {
        setCategoryError('Database table not found. Please push migrations to Supabase.');
        
        // Fallback to default categories
        setCategories([
          { categoryValue: 'assembly', categoryLabel: 'Assembly', isSystem: true },
          { categoryValue: 'production', categoryLabel: 'Production', isSystem: true },
          { categoryValue: 'integration', categoryLabel: 'Integration', isSystem: true },
          { categoryValue: 'quality', categoryLabel: 'Quality Control', isSystem: true }
        ]);
      } else {
        setCategoryError(err?.message || 'Failed to load categories');
      }
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      setTemplateError(null);
      const templatesData = await workflowTemplateService?.getAllTemplates();
      setTemplates(templatesData || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setTemplateError(err?.message || 'Failed to load templates');
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const categoryButtons = [
    { id: 'all', name: 'All Templates', icon: 'LayoutGrid' },
    ...categories?.map(cat => ({
      id: cat?.categoryValue,
      name: cat?.categoryLabel,
      icon: 'Tag'
    }))
  ];


  const filteredTemplates = templates?.filter((template) => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    template?.tags?.some((tag) => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || template?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[300]"
        onClick={onClose}
        aria-hidden="true" />
      <div className="fixed inset-4 md:inset-8 lg:inset-16 bg-card rounded-2xl shadow-2xl border border-border z-[301] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <Icon name="LayoutTemplate" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Workflow Templates</h2>
              <p className="caption text-muted-foreground">Start with pre-built workflows</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
            aria-label="Close templates">

            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 px-6 py-4 border-b border-border">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)} />

          </div>
          
          {categoryError && (
            <div className="flex items-center gap-2 px-4 py-2 bg-destructive/10 text-destructive rounded-xl">
              <Icon name="AlertCircle" size={18} />
              <span className="text-sm">{categoryError}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 overflow-x-auto">
            {isLoadingCategories ? (
              <div className="flex items-center gap-2 px-4 py-2">
                <Icon name="Loader2" size={18} className="animate-spin" />
                <span className="text-sm text-muted-foreground">Loading categories...</span>
              </div>
            ) : (
              categoryButtons?.map((category) =>
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all duration-250 ${
                selectedCategory === category?.id ?
                'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground hover:bg-muted/80'}`
                }>

                  <Icon name={category?.icon} size={18} />
                  <span className="text-sm font-medium">{category?.name}</span>
                </button>
              )
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {isLoadingTemplates ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Icon name="Loader2" size={64} color="var(--color-muted-foreground)" className="animate-spin" />
              <p className="mt-4 text-lg font-medium text-muted-foreground">Loading templates...</p>
            </div>
          ) : templateError ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Icon name="AlertCircle" size={64} color="var(--color-destructive)" />
              <p className="mt-4 text-lg font-medium text-destructive">Failed to load templates</p>
              <p className="mt-2 caption text-muted-foreground">{templateError}</p>
              <Button variant="outline" onClick={loadTemplates} className="mt-4">
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          ) : filteredTemplates?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Icon name="Search" size={64} color="var(--color-muted-foreground)" />
              <p className="mt-4 text-lg font-medium text-muted-foreground">No templates found</p>
              <p className="mt-2 caption text-muted-foreground">
                {templates?.length === 0 ? 'Create your first template to get started' : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredTemplates?.map((template) => {
                const nodeCount = Array.isArray(template?.nodes) ? template?.nodes?.length : 0;
                const thumbnailUrl = template?.thumbnail_data || template?.thumbnail || 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158';
                
                return (
                  <div
                    key={template?.id}
                    className="bg-muted rounded-xl overflow-hidden hover:shadow-lg transition-all duration-250 cursor-grab active:cursor-grabbing group"
                    draggable
                    onDragStart={(e) => {
                      e?.dataTransfer?.setData('application/json', JSON.stringify({
                        type: 'template',
                        template: template
                      }));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}>
                    <div className="aspect-video overflow-hidden bg-background">
                      <img
                        src={thumbnailUrl}
                        alt={`${template?.name} workflow template`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-250"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158';
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-semibold text-foreground">{template?.name}</h3>
                        {template?.is_system && (
                          <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded font-medium">
                            System
                          </span>
                        )}
                      </div>
                      <p className="caption text-muted-foreground mb-3 line-clamp-2">
                        {template?.description || 'No description available'}
                      </p>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon name="Workflow" size={14} color="var(--color-muted-foreground)" />
                          <span className="caption text-muted-foreground">{nodeCount} nodes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="FolderOpen" size={14} color="var(--color-muted-foreground)" />
                          <span className="caption text-muted-foreground capitalize">{template?.category || 'custom'}</span>
                        </div>
                      </div>
                      {template?.tags && template?.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {template?.tags?.slice(0, 3)?.map((tag, index) => (
                            <span
                              key={index}
                              className="caption px-2 py-1 rounded-lg bg-background text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                          {template?.tags?.length > 3 && (
                            <span className="caption px-2 py-1 rounded-lg bg-background text-muted-foreground">
                              +{template?.tags?.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            onTemplateSelect?.(template);
                            onClose();
                          }}
                          className="flex-1"
                        >
                          <Icon name="Plus" size={14} className="mr-1" />
                          Use Template
                        </Button>
                        <Button
                          variant={template?.is_system ? "outline" : "default"}
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            if (template?.is_system) {
                              alert('System templates cannot be edited. Please create a copy of this template to make changes.');
                              return;
                            }
                            onTemplateEdit?.(template);
                            onClose();
                          }}
                          className="flex-1"
                          disabled={template?.is_system}
                        >
                          <Icon name="Edit" size={14} className="mr-1" />
                          Edit Nodes
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <p className="caption text-muted-foreground">
            {filteredTemplates?.length} template{filteredTemplates?.length !== 1 ? 's' : ''} available
          </p>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </>
  );

};

export default WorkflowTemplates;